import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SignalEvent, SignalEventType } from "@sales-ops-amplify/shared";
import { listSignals, updateSignal, refreshMarketPulse } from "@/api/signals";
import { listProviders } from "@/api/providers";
import { useDiscoveryProfile } from "@/hooks/useDiscovery";
import { useClientPagination, useDebouncedValue } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import { cn } from "@/cn";

type Tab = "feed" | "watchlist";

const TYPE_LABELS: Record<string, string> = {
  hiring_growth: "Hiring",
  funding: "Funding",
  news: "News",
  tech: "Tech",
  regulatory: "Regulatory",
  intent: "Intent",
  other: "Other",
};

const TYPE_CHIPS: Array<SignalEventType | "all"> = [
  "all",
  "hiring_growth",
  "funding",
  "news",
  "tech",
  "regulatory",
  "intent",
  "other",
];

function isStub(signal: SignalEvent): boolean {
  return signal.raw?.stub === true;
}

export function SignalsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("feed");
  const [typeFilter, setTypeFilter] = useState<SignalEventType | "all">("all");
  const [hideStubs, setHideStubs] = useState(true);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);

  const { data: signals = [], isLoading, error } = useQuery({
    queryKey: ["signals", debouncedQ],
    queryFn: () => listSignals(100, debouncedQ || undefined),
    staleTime: 15_000,
  });

  const { data: profile } = useDiscoveryProfile();
  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: listProviders,
    staleTime: 60_000,
  });

  const selectedPulseIds = useMemo(() => {
    const selected = new Set(profile?.signalSources ?? []);
    return providers
      .filter(
        (p) =>
          p.type === "signal" &&
          p.enabled &&
          p.implemented !== false &&
          (selected.size === 0 || selected.has(p.providerId)),
      )
      .map((p) => p.providerId);
  }, [profile, providers]);

  const watchMutation = useMutation({
    mutationFn: ({ id, watchlisted }: { id: string; watchlisted: boolean }) =>
      updateSignal(id, { watchlisted }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["signals"] });
    },
  });

  const pulseMutation = useMutation({
    mutationFn: () =>
      refreshMarketPulse(
        selectedPulseIds.length ? { providerIds: selectedPulseIds } : undefined,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["signals"] });
    },
  });

  const filtered = useMemo(() => {
    let list = tab === "watchlist" ? signals.filter((s) => s.watchlisted) : signals;
    if (typeFilter !== "all") {
      list = list.filter((s) => s.type === typeFilter);
    }
    if (hideStubs) {
      list = list.filter((s) => !isStub(s));
    }
    return list;
  }, [signals, tab, typeFilter, hideStubs]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of signals) {
      if (hideStubs && isStub(s)) continue;
      counts[s.type] = (counts[s.type] ?? 0) + 1;
    }
    return counts;
  }, [signals, hideStubs]);

  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(filtered);

  return (
    <div>
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Signals</h1>
          <p className="text-muted-foreground">
            Hiring, funding, news, tech, regulatory, and intent events — star items to watchlist.
          </p>
        </div>
        <button
          type="button"
          onClick={() => pulseMutation.mutate()}
          disabled={pulseMutation.isPending}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          {pulseMutation.isPending ? "Refreshing…" : "Refresh market pulse"}
        </button>
      </header>
      {selectedPulseIds.length > 0 && (
        <p className="mb-3 text-xs text-muted-foreground">
          Sources: {selectedPulseIds.join(", ")}.{" "}
          <Link to="/settings/signals" className="text-brand hover:underline">
            Manage
          </Link>
          {" · "}
          <Link to="/settings/integrations" className="text-brand hover:underline">
            Credentials
          </Link>
        </p>
      )}
      {pulseMutation.isSuccess && (
        <p className="mb-4 text-sm text-muted-foreground">
          Added {pulseMutation.data.count} pulse signal
          {pulseMutation.data.count !== 1 ? "s" : ""}.
        </p>
      )}
      {pulseMutation.isError && (
        <p className="mb-4 text-sm text-destructive">
          {pulseMutation.error instanceof Error
            ? pulseMutation.error.message
            : "Market pulse failed"}
        </p>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search signals…"
          className="w-48 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
        {TYPE_CHIPS.map((id) => {
          const label = id === "all" ? "All" : TYPE_LABELS[id] ?? id;
          const count =
            id === "all"
              ? hideStubs
                ? signals.filter((s) => !isStub(s)).length
                : signals.length
              : typeCounts[id] ?? 0;
          return (
            <button
              key={id}
              type="button"
              onClick={() => {
                setTypeFilter(id);
                setPage(1);
              }}
              className={cn(
                "rounded-md border px-2.5 py-1 text-xs font-medium transition",
                typeFilter === id
                  ? "border-brand/40 bg-brand/10 text-brand"
                  : "border-border text-muted-foreground hover:bg-muted/20",
              )}
            >
              {label}
              {count > 0 && (
                <span className="ml-1 opacity-70">{count}</span>
              )}
            </button>
          );
        })}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={hideStubs}
            onChange={(e) => {
              setHideStubs(e.target.checked);
              setPage(1);
            }}
            className="rounded border-border"
          />
          Hide stub events
        </label>
      </div>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
        {([
          ["feed", "Feed", signals.filter((s) => !hideStubs || !isStub(s)).length],
          ["watchlist", "Watchlist", signals.filter((s) => s.watchlisted).length],
        ] as const).map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            onClick={() => {
              setTab(id);
              setPage(1);
            }}
            className={cn(
              "relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition",
              tab === id ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 text-xs",
                tab === id ? "bg-brand/20 text-brand" : "bg-muted/20 text-muted-foreground",
              )}
            >
              {count}
            </span>
            {tab === id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand" />
            )}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">Failed to load signals.</p>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-card px-4 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {tab === "watchlist"
              ? "No watchlisted signals yet."
              : "No signals yet — run Refresh market pulse or a Signal Scan on discovery."}
          </p>
          {tab === "feed" && (
            <Link
              to="/people"
              className="mt-3 inline-block text-sm text-brand hover:underline"
            >
              Go to People
            </Link>
          )}
        </div>
      )}

      {!isLoading && pageItems.length > 0 && (
        <>
          <ul className="mb-4 space-y-2">
            {pageItems.map((signal) => (
              <SignalRow
                key={signal.id}
                signal={signal}
                busy={watchMutation.isPending}
                onToggleWatch={() =>
                  watchMutation.mutate({
                    id: signal.id,
                    watchlisted: !signal.watchlisted,
                  })
                }
              />
            ))}
          </ul>
          <PaginationBar
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            showingFrom={showingFrom}
            showingTo={showingTo}
            totalItems={filtered.length}
          />
        </>
      )}
    </div>
  );
}

function SignalRow({
  signal,
  busy,
  onToggleWatch,
}: {
  signal: SignalEvent;
  busy: boolean;
  onToggleWatch: () => void;
}) {
  const stub = isStub(signal);
  return (
    <li
      className={cn(
        "rounded-lg border border-border bg-card px-4 py-3",
        stub && "opacity-70",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-400">
              {TYPE_LABELS[signal.type] ?? signal.type}
            </span>
            {signal.sourceProvider && (
              <span className="rounded-full bg-muted/30 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {signal.sourceProvider}
              </span>
            )}
            {stub && (
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-400">
                Stub
              </span>
            )}
            {signal.companyName &&
              (signal.accountSlug ? (
                <Link
                  to={`/user/accounts/${signal.accountSlug}`}
                  className="text-sm font-medium text-foreground hover:text-brand hover:underline"
                >
                  {signal.companyName}
                </Link>
              ) : (
                <span className="text-sm font-medium text-foreground">
                  {signal.companyName}
                </span>
              ))}
          </div>
          <p className="text-sm text-foreground">{signal.summary}</p>
          {signal.title && signal.title !== signal.summary && (
            <p className="mt-0.5 text-xs text-muted-foreground">{signal.title}</p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>{new Date(signal.createdAt).toLocaleString()}</span>
            {signal.evidenceUrl && (
              <a
                href={signal.evidenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand hover:underline"
              >
                Evidence
              </a>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onToggleWatch}
          disabled={busy}
          title={signal.watchlisted ? "Remove from watchlist" : "Add to watchlist"}
          className={cn(
            "shrink-0 rounded-md border px-2.5 py-1.5 text-xs font-medium transition disabled:opacity-50",
            signal.watchlisted
              ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
              : "border-border text-muted-foreground hover:bg-muted/20",
          )}
        >
          {signal.watchlisted ? "★ Watching" : "☆ Watch"}
        </button>
      </div>
    </li>
  );
}
